/**
 * Textes de l'éditeur de graphe des workflows, du lancement avec paramètres et de la page d'un run
 * (ADR 0102, 2026-11-03). Branchés dans `fr.ts` sous `workflowEditor` ; `workflow-editor.en.ts` porte
 * exactement les mêmes clés. Aucun gabarit `{{ … }}` ici : i18next les interpolerait — les exemples
 * de gabarit sont des constantes de code (`features/workflows/graph/templates.ts`).
 */
export const workflowEditorFr = {
  newTitle: 'Nouveau workflow',
  editTitle: 'Modifier {{name}}',
  subtitle: 'Dessinez les étapes et reliez-les : une étape part quand celles dont elle dépend ont fini.',
  openEditor: 'Éditeur de graphe',
  openWizard: 'Assistant pas à pas',
  editGraph: 'Modifier le graphe',
  tabs: { graph: 'Graphe', json: 'JSON' },
  actions: {
    autoLayout: 'Mise en page automatique',
    save: 'Enregistrer',
    create: 'Créer le workflow',
    cancel: 'Annuler',
    deleteStep: 'Supprimer l’étape',
    settings: 'Réglages du workflow',
  },
  palette: {
    title: 'Étapes',
    hint: 'Cliquez pour ajouter, puis reliez les poignées.',
    groups: { run: 'Lancer', flow: 'Contrôler le flot', call: 'Appeler un service' },
  },
  types: {
    job: 'Job',
    script: 'Script',
    workflow: 'Sous-workflow',
    condition: 'Condition',
    time: 'Attente',
    error: 'Erreur',
    http: 'Appel HTTP',
    agent: 'Agent IA',
    inference: 'Inférence',
    catalog: 'Catalogue',
    wait: 'Capteur',
    approval: 'Approbation',
  },
  typeUnknown: 'Type « {{type}} »',
  typeHelp: {
    job: 'Lance un job du projet et attend sa fin.',
    script: 'Exécute un script bash dans un pod ; ses valeurs arrivent par GRAAL_WORKFLOW_PARAMETERS.',
    workflow: 'Lance un autre workflow du projet et l’attend (5 niveaux au plus, sans cycle).',
    condition: 'Évalue une expression : fausse, l’étape et tout ce qui en dépend sont ignorés.',
    time: 'Attend une durée ou une date, sans pod.',
    error: 'Fait échouer le run dès qu’elle est atteinte.',
    http: 'Appelle une API par une connexion http du projet, ou une URL publique.',
    agent: 'Interroge un modèle de la passerelle, un work item de graal Agentic Platform ou un agent A2A.',
    inference:
      'Appelle un endpoint de serving, la passerelle LLM, un serveur compatible OpenAI ou KServe V2.',
    catalog: 'Agit sur OpenMetadata : ingestion, lecture d’une entité, qualité, lignage.',
    wait: 'Attend qu’un objet arrive, qu’une API réponde ou qu’un autre run réussisse.',
    approval: 'Attend la décision d’une personne : approuver ou rejeter.',
  },
  canvas: {
    label: 'Graphe du workflow',
    empty: 'Aucune étape : ajoutez-en une depuis la palette.',
    forEach: 'éventail',
    lock: 'verrou {{lock}}',
    problems_one: '{{count}} problème',
    problems_other: '{{count}} problèmes',
  },
  panel: {
    step: 'Étape',
    typeSection: 'Paramètres',
    commonSection: 'Exécution',
    targetSection: 'Cible',
    unknownType:
      'Type inconnu de la console : ses champs sont gardés tels quels à l’enregistrement. Modifiez-les dans l’onglet JSON.',
    depends: 'Dépend de',
    noDepends: 'Aucune étape amont : démarre le workflow.',
  },
  references: {
    title: 'Insérer une référence',
    help: 'Cliquez une référence : elle s’ajoute au dernier champ texte choisi, ou se copie.',
    upstream: 'Sorties d’une étape amont',
    noUpstream: 'Aucune étape amont : reliez-en une pour citer ses sorties.',
    params: 'Paramètres du run',
    item: 'Élément courant (éventail)',
    run: 'Le run',
    inserted: 'Référence ajoutée au champ « {{field}} ».',
    copied: 'Référence copiée.',
    syntax:
      'Gabarits : une expression entre doubles accolades. Filtres : json, default, urlencode. Une étape citée doit être en amont.',
  },
  common: {
    name: 'Nom de l’étape',
    description: 'Description',
    retries: 'Reprises après échec',
    retry_delay_seconds: 'Délai avant reprise (s)',
    timeout_seconds: 'Durée maximale (s)',
    trigger_rule: 'Règle de démarrage',
    for_each: 'Éventail (for_each)',
    max_parallel: 'Éléments en parallèle',
    lock: 'Verrou nommé',
    infrastructure_id: 'Infrastructure',
    device_id: 'Appareil',
  },
  commonHelp: {
    name: 'Lettres, chiffres, - et _ ; unique dans le workflow. Les étapes qui en dépendent suivent un renommage.',
    retries: '0 à 10 nouvelles tentatives.',
    timeout_seconds: 'Au-delà, la tentative échoue (puis les reprises s’appliquent).',
    trigger_rule:
      'Toutes réussies (défaut), toutes terminées quel que soit le résultat, ou au moins une en échec.',
    for_each:
      'Un gabarit qui rend une liste JSON, ou une liste littérale : une tentative par élément (1 000 au plus).',
    max_parallel: '1 à 64, 4 par défaut.',
    lock: 'Deux étapes qui portent le même verrou ne tournent jamais en même temps, dans tout le projet.',
    target: 'Remplace la cible du run pour cette étape (l’une OU l’autre).',
  },
  triggerRules: {
    all_success: 'Toutes réussies',
    all_done: 'Toutes terminées',
    one_failed: 'Au moins une en échec',
  },
  fields: {
    ref: 'Job',
    workflowRef: 'Workflow',
    parameters: 'Paramètres (nom=valeur)',
    script: 'Script',
    expression: 'Expression',
    duration_seconds: 'Durée (secondes)',
    until: 'Jusqu’à (date ISO 8601)',
    message: 'Message',
    connection_id: 'Connexion http',
    url: 'URL publique',
    method: 'Méthode',
    path: 'Chemin',
    query: 'Paramètres de requête (nom=valeur)',
    headers: 'En-têtes (nom=valeur)',
    body: 'Corps',
    content_type: 'Type du corps',
    expected_status: 'Statuts attendus',
    success_condition: 'Condition de succès',
    provider: 'Fournisseur',
    prompt: 'Instruction',
    system: 'Prompt système',
    model: 'Modèle',
    rag_index_id: 'Index documentaire (RAG)',
    output_format: 'Format de la réponse',
    temperature: 'Température',
    max_tokens: 'Jetons au plus',
    agent_project: 'Projet Agentic Platform',
    agent_workflow: 'Workflow Agentic Platform',
    title: 'Titre du work item',
    fields: 'Champs du work item (nom=valeur)',
    labels: 'Étiquettes',
    budget_usd: 'Budget (USD)',
    accept_outcomes: 'Issues acceptées',
    poll_interval_seconds: 'Intervalle de suivi (s)',
    endpoint_id: 'Endpoint',
    operation: 'Opération',
    input: 'Entrée (JSON)',
    action: 'Action',
    pipeline: 'Pipeline d’ingestion (FQN)',
    wait: 'Attendre la fin',
    entity: 'Entité (FQN)',
    entity_type: 'Type d’entité',
    max_failures: 'Échecs tolérés',
    upstream: 'Entités amont (FQN)',
    sensor: 'Capteur',
    condition: 'Condition',
    bucket_id: 'Bucket',
    prefix: 'Préfixe',
    min_count: 'Objets au moins',
    job_id: 'Job observé',
    workflow_id: 'Workflow observé',
    since: 'Depuis',
    poke_interval_seconds: 'Intervalle de vérification (s)',
    soft_fail: 'Ignorer au lieu d’échouer à l’échéance',
    approvers: 'Approbateurs (identifiants)',
    on_timeout: 'À l’échéance',
    forbid_self_approval: 'Quatre yeux : le lanceur du run ne décide pas',
  },
  help: {
    parameters: 'Une ligne par paramètre ; les valeurs acceptent les gabarits. Jamais un secret.',
    expression:
      'Grammaire fermée : steps.<étape>.status, steps.<étape>.outputs.<clé>, params.<nom>, comparaisons, ! && || et parenthèses.',
    duration_seconds: 'Exactement une durée OU une date.',
    connection_id:
      'Base, secret et adresses permises viennent de la connexion. Exactement une connexion OU une URL.',
    url: 'Une adresse publique seulement (les adresses privées sont refusées).',
    headers: 'Authorization, Cookie, Host et Proxy-* sont refusés : l’identité vient de la connexion.',
    expected_status: 'Défaut : tout 2xx.',
    success_condition: 'Lit response.status, response.body… et response.headers["nom"].',
    prompt: 'Accepte les gabarits : citez les sorties des étapes amont.',
    rag_index_id: 'Les passages retrouvés pour l’instruction sont donnés au modèle.',
    output_format: 'json : la réponse doit être un objet JSON, sinon la tentative échoue.',
    accept_outcomes: 'success (défaut), declined, cancelled.',
    input: 'Un gabarit qui rend une valeur JSON : corps de la requête, entrées ou messages.',
    max_failures: 'Au-delà, l’étape échoue.',
    condition: 'Défaut : un statut 2xx.',
    since: 'run_start (défaut), logical_date, ou une durée ISO 8601 (P1D).',
    approvers: 'Vide : quiconque peut lancer le workflow.',
    soft_fail: 'À l’échéance (timeout), l’étape est ignorée au lieu d’échouer.',
    forbid_self_approval:
      'La personne qui a lancé le run ne peut ni approuver ni rejeter cette étape : une autre doit décider.',
  },
  enums: {
    llm: 'Passerelle LLM',
    agentic_platform: 'graal Agentic Platform',
    a2a: 'Agent A2A',
    endpoint: 'Endpoint de serving',
    openai: 'Compatible OpenAI',
    kserve_v2: 'KServe V2 (Triton, MLServer)',
    openmetadata: 'OpenMetadata',
    text: 'Texte',
    json: 'JSON',
    chat: 'Chat',
    embeddings: 'Embeddings',
    completions: 'Complétion',
    run_ingestion: 'Lancer une ingestion',
    get_entity: 'Lire une entité',
    check_quality: 'Vérifier la qualité',
    add_lineage: 'Ajouter du lignage',
    http: 'Une API répond',
    object: 'Un objet arrive',
    run: 'Un run réussit',
    fail: 'Échouer',
    approve: 'Approuver',
    skip: 'Ignorer l’étape',
  },
  options: { none: '— aucun —' },
  settings: {
    title: 'Réglages du workflow',
    help: 'Cliquez une étape pour l’éditer ; cliquez le fond pour revenir ici.',
    name: 'Nom du workflow',
    description: 'Description',
    maxParallelTasks: 'Étapes en parallèle par run',
    maxParallelTasksHelp: '1 à 256 ; chaque élément d’un éventail compte. Vide : sans plafond.',
    maxActiveRuns: 'Runs actifs au plus',
    maxActiveRunsHelp: 'Au-delà, un lancement manuel est refusé et une échéance cron est sautée.',
    inputs: 'Paramètres déclarés',
    inputsHelp: 'Vérifiés à chaque lancement ; le formulaire « Lancer » est généré depuis eux.',
    addInput: 'Ajouter un paramètre',
    removeInput: 'Retirer ce paramètre',
    input: 'Paramètre {{position}}',
    schedule: 'Planification',
    scheduleHelp: 'Manuel, ou planifié par une expression cron ; mêmes champs que le résumé du workflow.',
  },
  notifications: {
    title: 'Notifications',
    help: 'Au démarrage, au succès, à l’échec, et les alertes du SLA. Un webhook nouveau reçoit un secret de signature, montré une fois à l’enregistrement.',
    empty: 'Aucune notification.',
    add: 'Ajouter une notification',
    addTitle: 'Nouvelle notification',
    addDescription: 'Elle part avec la définition, à l’enregistrement du workflow.',
    remove: 'Retirer cette notification',
    events: {
      on_start: 'Au démarrage',
      on_success: 'Au succès',
      on_failure: 'À l’échec',
      on_alert: 'Alerte SLA',
    },
  },
  sla: {
    title: 'Engagement de service (SLA)',
    help: 'Alertes en retard, lent, en échec puis rétabli, envoyées aux notifications « Alerte SLA ». Vide : aucun seuil.',
    maxAge: 'Retard après (s)',
    maxAgeHelp: 'Alerte quand le dernier succès est plus ancien (1 s à 366 jours).',
    slowFactor: 'Lent au-delà de p95 ×',
    slowFactorHelp:
      'Alerte quand un run dure plus que le p95 multiplié par ce facteur (1 à 100, 2 par défaut).',
    timeoutFraction: 'Lent au-delà de cette part du délai',
    timeoutFractionHelp:
      'Alerte quand un run dépasse cette fraction de son délai (plus de 0, au plus 1 ; 0,8 par défaut).',
  },
  summary: {
    graph: 'Graphe',
    graphLastRun: 'Couleurs du dernier run, {{run}}',
    graphNoRun: 'Pas encore de run',
    noSteps: 'Ce workflow n’a aucune étape.',
    runs: 'Exécutions',
    successRate: 'de réussite',
    noRuns: 'Jamais lancé',
    counts: '{{total}} runs · {{failed}} en échec · {{running}} en cours',
    lastRun: 'Dernier run',
    schedule: 'Planification',
    manual: 'Manuel ou par déclencheur',
    infrastructure: 'Infrastructure',
    inputs: 'Paramètres',
    noInputs: 'Aucun',
    labels: 'Étiquettes',
    identity: 'Identité d’exécution',
    updated: 'Modifié',
    description: 'Description',
    steps: 'Étapes',
    settings: {
      title: 'Nom, planification et reprises',
      hint: 'Le graphe, les notifications et le SLA se modifient dans l’éditeur.',
      edit: 'Modifier',
      close: 'Fermer',
    },
    cron: {
      everyMinute: 'Toutes les minutes',
      everyMinutes_one: 'Toutes les minutes',
      everyMinutes_other: 'Toutes les {{count}} minutes',
      hourly: 'Toutes les heures, à la minute {{minute}}',
      everyHours_one: 'Toutes les heures, à la minute {{minute}}',
      everyHours_other: 'Toutes les {{count}} heures, à la minute {{minute}}',
      daily: 'Tous les jours à {{time}}',
      weekly: '{{days}} à {{time}}',
      dayRange: 'du {{from}} au {{to}}',
      monthly: 'Le {{day}} de chaque mois à {{time}}',
    },
  },
  history: {
    tab: 'Historique',
    title: 'Historique des définitions',
    description:
      'Chaque création et chaque modification de la définition est une révision. Restaurer en enregistre une nouvelle.',
    empty: 'Aucune révision.',
    emptyHelp: 'Les révisions apparaissent à l’enregistrement du workflow.',
    columns: {
      revision: 'Révision',
      created: 'Enregistrée',
      author: 'Auteur',
      name: 'Nom',
      steps: 'Étapes',
    },
    stepCount_one: '{{count}} étape',
    stepCount_other: '{{count}} étapes',
    current: 'actuelle',
    view: 'Voir',
    viewLabel: 'Voir la révision {{revision}}',
    revisionTitle: 'Révision {{revision}}',
    revisionMeta: 'Enregistrée le {{date}} par {{author}}',
    graph: 'Graphe de la révision',
    identical: 'Identique à la définition actuelle.',
    changes_one: '{{count}} modification à restaurer',
    changes_other: '{{count}} modifications à restaurer',
    restore: 'Restaurer',
    confirmTitle: 'Restaurer la révision {{revision}} ?',
    confirmDescription_one:
      'La définition actuelle est remplacée par celle-ci ({{count}} modification). L’historique la garde : une nouvelle révision est écrite.',
    confirmDescription_other:
      'La définition actuelle est remplacée par celle-ci ({{count}} modifications). L’historique la garde : une nouvelle révision est écrite.',
    restored: 'Révision {{revision}} restaurée.',
    restoreFailed: 'La restauration a échoué.',
  },
  picker: {
    title: 'Dans quel projet ?',
    help: 'Un workflow vit dans un projet : choisissez celui où ouvrir l’éditeur.',
  },
  inputs: {
    name: 'Nom',
    type: 'Type',
    default: 'Valeur par défaut',
    required: 'Obligatoire',
    enum: 'Valeurs permises',
    description: 'Description',
    types: {
      string: 'Texte',
      number: 'Nombre',
      boolean: 'Booléen',
      json: 'JSON',
      date: 'Date',
    },
  },
  json: {
    label: 'Définition (JSON)',
    help: 'La définition envoyée à l’API. Appliquez pour revenir au graphe ; les champs inconnus sont gardés.',
    apply: 'Appliquer au graphe',
    applied: 'Définition appliquée au graphe.',
    invalidSyntax: 'JSON illisible : {{detail}}',
    invalidShape: 'Un objet est attendu, et « tasks » doit être une liste d’objets.',
  },
  validation: {
    title: 'Validation',
    checking: 'Vérification par le serveur…',
    valid: 'Définition valide',
    local_one: '{{count}} problème à corriger',
    local_other: '{{count}} problèmes à corriger',
    server: 'Refus du serveur',
    serverUnavailable: 'Validation du serveur indisponible : seule la vérification locale s’applique.',
    levels_one: '{{count}} niveau d’exécution',
    levels_other: '{{count}} niveaux d’exécution',
    workflow: 'Workflow',
    blocked: 'Corrigez les problèmes signalés avant d’enregistrer.',
  },
  problems: {
    noSteps: 'Le workflow n’a aucune étape.',
    nameRequired: 'Le nom est obligatoire.',
    nameInvalid: 'Nom invalide : lettres, chiffres, - et _, 63 caractères au plus.',
    nameDuplicate: 'Deux étapes portent ce nom.',
    dependencyUnknown: 'Dépend de « {{step}} », qui n’existe pas.',
    dependencySelf: 'Une étape ne peut pas dépendre d’elle-même.',
    cycle: 'L’étape est dans un cycle (ou derrière un cycle).',
    fieldRequired: 'Champ obligatoire : {{field}}.',
    exactlyOne: 'Exactement un de « {{a}} » ou « {{b}} ».',
    atMostOne: 'Au plus un de « {{a}} » ou « {{b}} ».',
    tooLong: '{{field}} : {{max}} caractères au plus.',
    range: '{{field}} : entre {{min}} et {{max}}.',
    notInteger: '{{field}} : un nombre entier est attendu.',
    invalidInstant: 'Une date ISO 8601 avec fuseau est attendue (2026-11-04T06:00:00Z).',
    invalidLock: 'Verrou invalide : lettres, chiffres, _ . : -, 128 caractères au plus.',
    invalidStatus: 'Statuts HTTP entre 100 et 599.',
    invalidEnum: '{{field}} : valeur non permise.',
    forbiddenHeader: 'En-tête « {{header}} » refusé : l’identité vient de la connexion.',
    forEachNotAllowed: 'Ce type d’étape n’accepte pas d’éventail.',
    templateUnclosed: '{{field}} : gabarit non refermé.',
    templateNotUpstream: '{{field}} : « {{step}} » n’est pas une étape en amont.',
    templateItemOutsideForEach: '{{field}} : « item » n’existe que dans une étape à éventail.',
    workflowNameRequired: 'Le nom du workflow est obligatoire.',
    workflowNameInvalid: 'Nom du workflow invalide : lettres, chiffres, - _ et ., 63 caractères au plus.',
    inputNameInvalid: 'Nom de paramètre invalide (lettre ou _, puis lettres, chiffres, _ . -).',
    inputDuplicate: 'Deux paramètres portent ce nom.',
    inputDefaultNotAllowed: 'La valeur par défaut n’est pas une valeur permise.',
    inputDefaultType: 'La valeur par défaut n’est pas du type déclaré.',
    cronInvalid: 'Expression cron invalide.',
    slaFraction: '{{field}} : plus de 0, au plus 1.',
  },
  saved: 'Workflow enregistré.',
  created: 'Workflow « {{name}} » créé.',
  saveFailed: 'L’enregistrement a échoué.',
  unchanged: 'Aucune modification à enregistrer.',
  launch: {
    title: 'Lancer le workflow',
    description: 'Les paramètres déclarés sont vérifiés avant l’envoi ; une valeur vide prend son défaut.',
    submit: 'Lancer',
    infrastructure: 'Infrastructure',
    defaultValue: 'défaut : {{value}}',
    notAllowed: 'Valeur non permise.',
    invalid: {
      string: 'Valeur invalide.',
      number: 'Un nombre est attendu.',
      boolean: 'true ou false.',
      json: 'Un JSON valide est attendu.',
      date: 'Une date ISO 8601 est attendue (2026-11-03).',
    },
    noInfrastructure: 'Aucune infrastructure utilisable : un run ne peut pas partir.',
  },
  run: {
    graph: 'Graphe du run',
    graphHint: 'Couleur = statut de l’étape. Cliquez une étape pour ses tentatives, sorties et progression.',
    select: 'Cliquez une étape du graphe.',
    attempts: 'Tentatives',
    attempt: 'Tentative {{number}}',
    outputs: 'Sorties',
    noOutputs: 'Aucune sortie.',
    progress: 'Progression',
    error: 'Erreur',
    items_one: '{{count}} élément',
    items_other: '{{count}} éléments',
    item: 'Élément {{index}}',
    notStarted: 'Pas encore démarrée.',
    openChildRun: 'Ouvrir le run',
    approvals: {
      title: 'Approbations en attente',
      waiting: 'L’étape « {{step}} » attend une décision.',
      approve: 'Approuver',
      reject: 'Rejeter',
      comment: 'Commentaire',
      commentHelp: 'Rendu dans les sorties de l’étape ; c’est la raison de l’échec d’un rejet.',
      approveTitle: 'Approuver « {{step}} »',
      rejectTitle: 'Rejeter « {{step}} »',
      dialogDescription: 'La décision est définitive : le run reprend (approuvé) ou l’étape échoue (rejeté).',
      approved: 'Étape « {{step}} » approuvée.',
      rejected: 'Étape « {{step}} » rejetée.',
      failed: 'La décision n’a pas été enregistrée.',
      notWaiting: 'Cette étape n’attend plus de décision.',
      forbidden:
        'Décision refusée : vous avez lancé ce run et l’étape exige quatre yeux, ou vous n’êtes pas parmi les approbateurs.',
    },
  },
} as const;
